import React, { Component } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Button
} from 'reactstrap';
import {
  ComposableMap,
  Geographies,
  Geography,
} from 'react-simple-maps';
import { geoMercator } from 'd3-geo';
import { feature as topojsonFeature } from 'topojson-client';
import { UncontrolledTooltip } from 'reactstrap';

class Map extends Component {
  constructor(props) {
    super(props);
    this.state = {
      province: null,
      headerText: 'Provinces in China',
      geographyPath: '/geographies/china.json',
      featureCollection: null,
      geographies: [],
      mapWidth: 900,
      mapHeight: 620
    };

    this.containerRef = React.createRef();
    this.handleResize = this.handleResize.bind(this);
    this.handleGeoClick = this.handleGeoClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
  }

  componentDidMount() {
    this.setupResizeTracking();
    this.syncScopeFromProps();
  }

  componentDidUpdate(prevProps) {
    if(prevProps.province !== this.props.province) {
      this.syncScopeFromProps();
    }
  }

  componentWillUnmount() {
    if(this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setupResizeTracking() {
    this.handleResize();

    if(typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.containerRef.current);
    } else {
      window.addEventListener('resize', this.handleResize);
    }
  }

  handleResize() {
    const node = this.containerRef.current;
    if(!node) return;

    const rect = node.getBoundingClientRect();
    const nextWidth = Math.max(360, Math.round(rect.width || node.clientWidth || 900));
    const nextHeight = Math.max(360, Math.round((rect.height || node.clientHeight || nextWidth * 0.62)));

    if(nextWidth !== this.state.mapWidth || nextHeight !== this.state.mapHeight) {
      this.setState({ mapWidth: nextWidth, mapHeight: nextHeight });
    }
  }

  syncScopeFromProps() {
    const { province } = this.props;

    if(province) {
      this.setState({
        province: province.attributes,
        headerText: 'Prefectures in ' + province.attributes.name,
        geographyPath: '/geographies/provinces/' + province.attributes.name + '.json'
      }, () => this.loadGeography(this.state.geographyPath));
      return;
    }

    this.setState({
      province: null,
      headerText: 'Provinces in China',
      geographyPath: '/geographies/china.json'
    }, () => this.loadGeography(this.state.geographyPath));
  }

  loadGeography(path) {
    fetch(path).then((resp) => resp.json())
      .then((topology) => {
        const objectName = Object.keys(topology.objects)[0];
        const featureCollection = topojsonFeature(topology, topology.objects[objectName]);

        this.setState({
          featureCollection,
          geographies: featureCollection.features
        }, () => {
          window.requestAnimationFrame(this.handleResize);
        });
      });
  }

  buildProjection(width, height) {
    const { featureCollection } = this.state;
    const projection = geoMercator();

    if(featureCollection && featureCollection.features && featureCollection.features.length > 0) {
      const inset = Math.max(20, Math.round(Math.min(width, height) * 0.05));
      projection.fitExtent([[inset, inset], [width - inset, height - inset]], featureCollection);
    }

    return projection;
  }

  handleGeoClick(geoObj) {
    if(this.state.province) {
      fetch('/api/prefectures?filter[name]=' + encodeURIComponent(geoObj.name)).then((resp) => resp.json())
        .then((respJson) => {
          respJson.data.forEach((item) => {
            fetch(item.relationships.province.links.related).then((resp) => resp.json())
              .then((provinceResp) => {
                if(provinceResp.data.attributes.name === this.state.province.name) {
                  this.props.updatePrefectures({ prefecture: item });
                }
              });
          });
        });
    } else {
      fetch('/api/provinces?filter[name]=' + encodeURIComponent(geoObj.name)).then((resp) => resp.json())
        .then((respJson) => {
          respJson.data.forEach((item) => {
            this.props.updatePrefectures({
              province: item,
              prefecture: null
            });
          });
        });
    }
  }

  handleBackClick() {
    if(this.state.province) {
      this.props.updatePrefectures({
        province: null,
        prefecture: null
      });
    }
  }

  render() {
    const { prefecture } = this.props;
    const { geographies, mapWidth, mapHeight } = this.state;
    const helperText = this.state.province
      ? 'Prefecture geography within the selected province.'
      : 'National and provincial geography view.';

    return (
      <Card className="mb-0 pref-workspace-card sl-panel sl-map-panel">
        <CardHeader className="pref-workspace-head">
          <div>
            <div className="pref-workspace-kicker">Map Workspace</div>
            <div className="pref-workspace-title">{this.state.headerText}</div>
            <p className="pref-workspace-copy mb-0">{helperText}</p>
          </div>
          <div className="sl-explorer-workspace-actions">
            <span className="pref-workspace-badge">Interactive</span>
            {this.state.province ? (
              <Button
                className="sl-inline-action"
                size="sm"
                onClick={this.handleBackClick}>
                Back
              </Button>
            ) : null}
          </div>
        </CardHeader>

        <div className="pref-workspace-map-shell sl-explorer-map-shell">
          <CardBody className="pref-map-card-body">
            <div className="w-100 my-0 mx-auto sl-explorer-map-canvas" ref={this.containerRef}>
              <ComposableMap
                projection={(width, height) => this.buildProjection(width, height)}
                width={mapWidth}
                height={mapHeight}
                style={{
                  width: '100%',
                  height: '100%'
                }}>
                <Geographies geography={geographies} disableOptimization>
                  {(loadedGeographies, projection) => loadedGeographies.map((geography, i) => {
                    if(geography.id === 'ATA') return null;

                    let defaultFill = '#ECEFF1';
                    if(prefecture && geography.properties.name === prefecture.attributes.name) {
                      defaultFill = '#215796';
                    }

                    if(geography.properties.notAvailable) {
                      return (
                        <Geography
                          key={i}
                          id={'geo-' + i}
                          geography={geography}
                          projection={projection}
                          style={{
                            default: {
                              fill: defaultFill,
                              stroke: '#215796',
                              strokeWidth: 0.75,
                              opacity: 0.3,
                              outline: 'none',
                            },
                            hover: {
                              fill: defaultFill,
                              stroke: '#215796',
                              strokeWidth: 0.75,
                              opacity: 0.3,
                              outline: 'none',
                            },
                            pressed: {
                              fill: defaultFill,
                              stroke: '#215796',
                              strokeWidth: 0.75,
                              opacity: 0.3,
                              outline: 'none',
                            },
                          }}>
                          <UncontrolledTooltip placement="bottom" delay={0} target={'geo-' + i}>
                            {geography.properties.name} (Coming Soon)
                          </UncontrolledTooltip>
                        </Geography>
                      );
                    }

                    return (
                      <Geography
                        key={i}
                        id={'geo-' + i}
                        geography={geography}
                        projection={projection}
                        onClick={(geo) => this.handleGeoClick(geo.properties)}
                        style={{
                          default: {
                            fill: defaultFill,
                            stroke: '#215796',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                          hover: {
                            fill: '#215796',
                            stroke: '#215796',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                          pressed: {
                            fill: '#e25241',
                            stroke: '#215796',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                        }}>
                        <UncontrolledTooltip placement="bottom" delay={0} target={'geo-' + i}>
                          {geography.properties.name}
                        </UncontrolledTooltip>
                      </Geography>
                    );
                  })}
                </Geographies>
              </ComposableMap>
            </div>
          </CardBody>
        </div>

        <div className="pref-workspace-footer">
          <div className="pref-workspace-stat">
            <div className="pref-workspace-stat-label">Mode</div>
            <div className="pref-workspace-stat-value">{this.state.province ? 'Prefecture' : 'Province'}</div>
            <div className="pref-workspace-stat-note">{this.state.province ? 'Province selected' : 'China-wide overview'}</div>
          </div>
          <div className="pref-workspace-stat">
            <div className="pref-workspace-stat-label">Selection</div>
            <div className="pref-workspace-stat-value">{prefecture ? prefecture.attributes.name : this.state.province ? this.state.province.name : 'None yet'}</div>
            <div className="pref-workspace-stat-note">{prefecture ? 'Active market read' : 'Choose a region on the map'}</div>
          </div>
          <div className="pref-workspace-stat">
            <div className="pref-workspace-stat-label">Next Step</div>
            <div className="pref-workspace-stat-value">{this.state.province ? 'Review Market Read' : 'Choose a Province'}</div>
            <div className="pref-workspace-stat-note">{this.state.province ? 'Then open a prefecture profile' : 'Then narrow into prefectures'}</div>
          </div>
        </div>
      </Card>
    );
  }
}

export default Map;
